import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import { clientApi } from "@/http/ClientApi";
import { globals } from "@returns-centre/globals";
import { getModule } from "vuex-module-decorators";
import {
    AppModule,
    CustomerModule,
    ShopModule,
    store,
    TradeInModule
} from "@returns-centre/store";

import ReturnsCentre from "./ReturnsCentre.vue";
import VerifyOrder from "./VerifyOrder.vue";
import TradeInWizard from "./TradeInWizard.vue";
import ReturnsInformation from "./ReturnsInformation.vue";
import TradeIns from "./TradeIns.vue";
import TradeInDetails from "./TradeInDetails.vue";
import {loadLanguageAsync} from "@/i18n";

Vue.use(VueRouter);

const shopModule = getModule(ShopModule, store);
const customerModule = getModule(CustomerModule, store);
const appModule = getModule(AppModule, store);
const tradeInModule = getModule(TradeInModule, store);

const resolveSession = (async function() {
    return appModule.resolveSession();
})();

let landingPath: string = globals.logged_in_customer_id
    ? "/trade-in"
    : "/verify-order";

if (tradeInModule.hasTradeInStep) {
    landingPath = "/trade-in";
}

const routes: RouteConfig[] = [
    {
        path: "/",
        name: "ReturnsCentre",
        component: ReturnsCentre,
        redirect: landingPath,
        children: [
            {
                path: "/verify-order",
                name: "VerifyOrder",
                component: VerifyOrder,
                meta: {
                    routeNotAllowedWhenLoggedIn: true
                },
                beforeEnter: (to, from, next) => {
                    const shop = shopModule.getShop;
                    if (!shop?.bought_online_active) {
                        return next({ name: 'TradeInRegister' });
                    }

                    next();
                }
            },
            {
                path: "/returns-information",
                name: "ReturnsInformation",
                component: ReturnsInformation
            },
            {
                path: "/trade-ins",
                name: "TradeIns",
                component: TradeIns,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: "/trade-ins/:id",
                name: "TradeInDetails",
                component: TradeInDetails,
                meta: {
                    requiresAuth: true
                }
            },
            {
                path: "/trade-in",
                name: "TradeInWizard",
                component: TradeInWizard,
                meta: {
                    requiresAuthOrVerifiedOrder: true
                }
            },
            {
                path: "/product-registration",
                name: "TradeInRegister",
                component: TradeInWizard
            }
        ]
    }
];

const router = new VueRouter({
    routes,
    scrollBehavior: (to, from, savedPosition) => {
        if (savedPosition) {
            return savedPosition;
        }
        if (to.hash) {
            return { selector: to.hash };
        }
        return { x: 0, y: 0 };
    }
});

router.beforeEach(async(to, from, next) => {
    const lang = to.params.lang;
    const shop: Shop = await clientApi.getShop(globals.shop_domain);
    if (!shop) {
        return false;
    }
    shopModule.setShop(shop);

    customerModule.setReturnsPortalShopifyCustomer(globals.shopify_customer);
    if (to.matched.some((record) => record.meta.requiresAuth)) {
        if (!globals.logged_in_customer_id) {
            loadLanguageAsync(lang).then(() =>  next({ name: "VerifyOrder" }));
        }
    }

    if (to.matched.some((record) => record.meta.routeNotAllowedWhenLoggedIn)) {
        if (globals.logged_in_customer_id) {
            loadLanguageAsync(lang).then(() =>  next({ name: "TradeInWizard" }));
        }
    }

    if (to.matched.some((record) => record.meta.requiresAuthOrVerifiedOrder)) {
        if (!globals.logged_in_customer_id && !customerModule.hasVerifiedOrder) {
            loadLanguageAsync(lang).then(() =>  next({ name: "VerifyOrder" }));
        }
    }

    loadLanguageAsync(lang).then(() => next());
});

export default router;
